import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import api from "app/api";
import useExcelUrl from "app/useExcelUrl";
import { DateRangePicker, PageHeader, Toolbar } from "common";
import { globalMessageHandler } from "common/components/GlobalMessaging";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import useHeaderHeight from "helpers/useHeaderHeight";
import I18n from "i18n";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

type CoordinatorReportItem = {
  event_id: number;
  start_time: string;
  closed_at: string | null;
  program_name: string;
  total_bookings: number;
  total_guests: number;
  street_bookings: number;
  street_guests: number;
};

type CoordinatorReportFilter = {
  from: string;
  to: string;
  programIds?: number[];
  boatIds?: number[];
};

export interface CoordinatorReportProps {}

const CoordinatorReport: React.FC<CoordinatorReportProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const headerHeight = useHeaderHeight();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CoordinatorReportItem[]>([]);
  const [filter, setFilter] = useState<CoordinatorReportFilter>({
    from: moment().startOf("week").format("YYYY-MM-DD"),
    to: moment().endOf("week").format("YYYY-MM-DD"),
    programIds: [],
    boatIds: [],
  });

  const requestParams = useMemo(
    () => ({
      from: filter.from,
      to: filter.to,
      programIds: filter.programIds,
      boatIds: filter.boatIds,
    }),
    [filter],
  );

  const excelUrl = useExcelUrl(
    "reports/coordinator",
    null,
    requestParams,
    false,
  );

  async function load() {
    setLoading(true);
    const { success, data, error } = await api.get<CoordinatorReportItem[]>(
      "reports/coordinator",
      {
        params: requestParams,
      },
    );
    setLoading(false);

    if (success) {
      setRows(data || []);
      return;
    }

    globalMessageHandler.alert({
      severity: "error",
      message: typeof error === "string" ? error : JSON.stringify(error),
    });
  }

  useEffect(() => {
    load();
  }, [requestParams]);

  return (
    <>
      <PageHeader
        title={I18n.t("CoordinatorReport.title", {
          defaultValue: "Koordi riport",
        })}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton onClick={load}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </PageHeader>

      {loading && (
        <LinearProgress
          sx={{ position: "fixed", left: 0, top: headerHeight, right: 0 }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          ml: isMobile ? -3 : 0,
          mr: isMobile ? -3 : 0,
          "& .MuiPaper-root": {
            borderRadius: isMobile ? 0 : undefined,
          },
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Toolbar
            sx={{ gap: 2, flexWrap: "wrap", justifyContent: "flex-start" }}
          >
            <DateRangePicker
              sx={{ width: 240 }}
              defaultRangeIdStorageKey="coordinatorReportDateRange"
              value={{ from: filter.from, to: filter.to }}
              onChange={range => {
                setFilter(prev => ({
                  ...prev,
                  ...(range as any),
                }));
              }}
            />
          </Toolbar>
          <Box sx={{ pt: 1 }}>
            <BoatCheckboxSelector
              color={theme.palette.primary.main}
              value={filter.boatIds || []}
              onChange={evt =>
                setFilter(prev => ({
                  ...prev,
                  boatIds: (evt.target?.value ? evt.target?.value : []) as any,
                }))
              }
            />
          </Box>
          <Box sx={{ pt: 1, pl: 2 }}>
            <ProgramCheckboxSelector
              value={filter.programIds || []}
              onChange={evt =>
                setFilter(prev => ({
                  ...prev,
                  programIds: (evt.target?.value
                    ? evt.target?.value
                    : []) as any,
                }))
              }
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 0 }}>
          <Toolbar
            sx={{
              justifyContent: "flex-end",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              component="a"
              href={excelUrl}
              color="inherit"
              size="small"
              download
              startIcon={
                <svg
                  style={{ width: 20, height: 20, fill: "currentColor" }}
                  viewBox="0 0 24 24"
                >
                  <path d="M 12 3 L 2 5 L 2 19 L 12 21 L 12 3 z M 14 5 L 14 7 L 16 7 L 16 9 L 14 9 L 14 11 L 16 11 L 16 13 L 14 13 L 14 15 L 16 15 L 16 17 L 14 17 L 14 19 L 21 19 C 21.552 19 22 18.552 22 18 L 22 6 C 22 5.448 21.552 5 21 5 L 14 5 z M 18 7 L 20 7 L 20 9 L 18 9 L 18 7 z M 4.1757812 8.296875 L 5.953125 8.296875 L 6.8769531 10.511719 C 6.9519531 10.692719 7.0084063 10.902625 7.0664062 11.140625 L 7.0917969 11.140625 C 7.1247969 10.997625 7.1919688 10.779141 7.2929688 10.494141 L 8.3222656 8.296875 L 9.9433594 8.296875 L 8.0078125 11.966797 L 10 15.703125 L 8.2714844 15.703125 L 7.1582031 13.289062 C 7.1162031 13.204062 7.0663906 13.032922 7.0253906 12.794922 L 7.0097656 12.794922 C 6.9847656 12.908922 6.934375 13.079594 6.859375 13.308594 L 5.7363281 15.703125 L 4 15.703125 L 6.0605469 11.996094 L 4.1757812 8.296875 z M 18 11 L 20 11 L 20 13 L 18 13 L 18 11 z M 18 15 L 20 15 L 20 17 L 18 17 L 18 15 z" />
                </svg>
              }
            >
              Export
            </Button>
          </Toolbar>

          <TableContainer>
            <Table
              size="small"
              sx={{
                "& td": { padding: "4px 8px" },
                "& tr:hover": {
                  backgroundColor: theme => theme.palette.action.hover,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 2 }}>Indulás időpontja</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell>Zárás időpontja</TableCell>
                  <TableCell align="right">Foglalások száma</TableCell>
                  <TableCell align="right">Létszám</TableCell>
                  <TableCell align="right">Utca foglalások</TableCell>
                  <TableCell align="right">Utca létszám</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.event_id}>
                    <TableCell sx={{ pl: 2 }}>{row.start_time}</TableCell>
                    <TableCell>{row.program_name}</TableCell>
                    <TableCell>{row.closed_at || "–"}</TableCell>
                    <TableCell align="right">
                      {row.total_bookings || 0}
                    </TableCell>
                    <TableCell align="right">{row.total_guests || 0}</TableCell>
                    <TableCell align="right">
                      {row.street_bookings || 0}
                    </TableCell>
                    <TableCell align="right">
                      {row.street_guests || 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {rows.length === 0 && !loading && (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">
                Nincs találat a megadott szűrőkre.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default CoordinatorReport;
